import React, { useMemo } from 'react';
import {
  CapaPcbServiceType,
  CapaQuotationDetailedServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';
import { formatDate } from '@utils/formatter';
import { CustomizedTypography } from '@components/Customized';
import { RfqDetailData } from '@routes/Common/type';
import { Box, makeStyles } from '@material-ui/core';
import { requestEnumToString } from '@utils/enumToString';

const useStyles = makeStyles(() => {
  return {
    dispalayItem: {
      display: 'flex',
      alignItems: 'center',
      '&:not(:last-child)': {
        marginBottom: 8
      }
    },
    itemLabel: {
      width: 90
    }
  };
});

interface Props {
  data?: RfqDetailData;
  matches?: boolean;
}

const pcbOrder = [
  CapaPcbServiceType.CircuitDesign,
  CapaPcbServiceType.CircuitFabrication,
  CapaPcbServiceType.CircuitAssembly
];

const RfqDetailInfoPresenter: React.FC<Props> = ({ data, matches }) => {
  const classes = useStyles({ matches: matches });
  const serviceType = data?.serviceType;
  const detailedServiceType = data?.detailedServiceType;
  const detailedServiceTypeText = requestEnumToString(
    detailedServiceType as CapaQuotationDetailedServiceType
  );

  const optionalServices = [
    ...(data?.optionalServices ?? []).map((service) => service.serviceType),
    ...(data?.design?.services ?? []).map((service) => service.serviceType)
  ];
  const optionalServiceQuantity =
    data?.optionalServices?.find(
      (service) => service.serviceType === CapaQuotationServiceType.Prototyping
    )?.quantity ?? 0;

  const optionalServicesText = optionalServices
    .map((v) => requestEnumToString(v as CapaQuotationServiceType))
    .join(', ');

  const dueDate = data?.dueDate;
  const dueNegotiable = data?.dueNegotiable;
  const isDesign = serviceType === CapaQuotationServiceType.Design;
  const isPcb = serviceType === CapaQuotationServiceType.Pcb;
  const pcbServices = (data?.pcb?.services ?? []).map(
    (service) => service.serviceType
  );
  const projectName = data?.projectName;
  const pcbServiceTypeText = useMemo(() => {
    const sortedPcbServices: CapaPcbServiceType[] = [];

    pcbOrder.forEach((service) => {
      if (pcbServices.includes(service)) {
        sortedPcbServices.push(service);
      }
    });

    return sortedPcbServices
      .map((service) => requestEnumToString(service as CapaPcbServiceType))
      .join(' · ');
  }, [pcbServices]);
  const hasCircuitDesign = pcbServices?.includes(
    CapaPcbServiceType.CircuitDesign
  );
  const hasCircuitFabrication = pcbServices?.includes(
    CapaPcbServiceType.CircuitFabrication
  );
  const hasCircuitAssembly = pcbServices?.includes(
    CapaPcbServiceType.CircuitAssembly
  );

  return (
    <Box className="item">
      <CustomizedTypography
        variant={'h3'}
        color="textPrimary"
        fontWeight="bold"
        gutterBottoms={16}
      >
        {projectName}
      </CustomizedTypography>
      <div className={classes.dispalayItem}>
        <CustomizedTypography
          className={classes.itemLabel}
          variant={'body1'}
          color="textSecondary"
          fontWeight="normal"
        >
          제조서비스
        </CustomizedTypography>
        <CustomizedTypography
          variant={'body1'}
          color="textPrimary"
          fontWeight="normal"
        >
          {requestEnumToString(serviceType as CapaQuotationServiceType)}
        </CustomizedTypography>
      </div>
      {isDesign ? (
        optionalServices.length > 0 && (
          <>
            <div className={classes.dispalayItem}>
              <CustomizedTypography
                className={classes.itemLabel}
                variant={'body1'}
                color="textSecondary"
                fontWeight="normal"
              >
                요청작업
              </CustomizedTypography>
              <CustomizedTypography
                variant={'body1'}
                color="textPrimary"
                fontWeight="500"
              >
                {optionalServicesText?.replace(/,/g, ' · ')}
              </CustomizedTypography>
            </div>
            {optionalServiceQuantity > 0 && (
              <div className={classes.dispalayItem}>
                <CustomizedTypography
                  className={classes.itemLabel}
                  variant={'body1'}
                  color="textSecondary"
                  fontWeight="normal"
                >
                  시제품수량
                </CustomizedTypography>
                <CustomizedTypography
                  variant={'body1'}
                  color="textPrimary"
                  fontWeight="500"
                >
                  {Number(optionalServiceQuantity).toLocaleString('kr')}개
                </CustomizedTypography>
              </div>
            )}
          </>
        )
      ) : isPcb ? (
        <div className={classes.dispalayItem}>
          <CustomizedTypography
            className={classes.itemLabel}
            variant={'body1'}
            color="textSecondary"
            fontWeight="normal"
          >
            요청작업
          </CustomizedTypography>
          <CustomizedTypography variant={'body1'} fontWeight="500">
            {pcbServiceTypeText}
          </CustomizedTypography>
        </div>
      ) : (
        <div className={classes.dispalayItem}>
          <CustomizedTypography
            className={classes.itemLabel}
            variant={'body1'}
            color="textSecondary"
            fontWeight="normal"
          >
            가공방식
          </CustomizedTypography>
          <CustomizedTypography
            variant={'body1'}
            color="textPrimary"
            fontWeight="500"
          >
            {detailedServiceTypeText}
          </CustomizedTypography>
        </div>
      )}

      {hasCircuitFabrication && (
        <div className={classes.dispalayItem}>
          <CustomizedTypography
            className={classes.itemLabel}
            variant={'body1'}
            color="textSecondary"
            fontWeight="normal"
          >
            제작수량
          </CustomizedTypography>
          <CustomizedTypography variant={'body1'} fontWeight="500">
            {`${
              hasCircuitDesign
                ? Number(data?.pcb?.fabricationQuantity).toLocaleString('kr')
                : (data?.pcb?.fabricationFiles ?? [])
                    .reduce((acc, cur) => (acc += Number(cur.quantity)), 0)
                    .toLocaleString('kr')
            }개`}
          </CustomizedTypography>
        </div>
      )}

      {!isDesign && !isPcb && (
        <div className={classes.dispalayItem}>
          <CustomizedTypography
            className={classes.itemLabel}
            variant={'body1'}
            color="textSecondary"
            fontWeight="normal"
          >
            총 수량
          </CustomizedTypography>
          <CustomizedTypography variant={'body1'} fontWeight="500">
            {`${(data?.drawingFiles ?? [])
              .reduce(
                (sum, drawingFile) =>
                  Number(drawingFile.quantity!) + Number(sum),
                0
              )
              .toLocaleString('kr')}개`}
          </CustomizedTypography>
        </div>
      )}

      {hasCircuitAssembly && (
        <div className={classes.dispalayItem}>
          <CustomizedTypography
            className={classes.itemLabel}
            variant={'body1'}
            color="textSecondary"
            fontWeight="normal"
          >
            조립수량
          </CustomizedTypography>
          <CustomizedTypography variant={'body1'} fontWeight="500">
            {`${
              hasCircuitDesign
                ? Number(data?.pcb?.assemblyQuantity).toLocaleString('kr')
                : (data?.pcb?.assemblyFiles ?? [])
                    .reduce((acc, cur) => (acc += Number(cur.quantity)), 0)
                    .toLocaleString('kr')
            }개`}
          </CustomizedTypography>
        </div>
      )}
      <div className={classes.dispalayItem}>
        <CustomizedTypography
          className={classes.itemLabel}
          variant={'body1'}
          color="textSecondary"
          fontWeight="normal"
        >
          희망납기일
        </CustomizedTypography>
        <CustomizedTypography
          variant={'body1'}
          color="textPrimary"
          fontWeight="normal"
        >
          {dueDate ? formatDate(dueDate, 'YYYY.MM.DD') : '-'} 까지 납기
          {dueNegotiable ? '(협의 가능)' : ''}
        </CustomizedTypography>
      </div>
    </Box>
  );
};

export default RfqDetailInfoPresenter;
